import React, { ChangeEvent, KeyboardEvent, Suspense, lazy, useEffect, useRef, useState } from 'react';
import './App.scss';
import 'bootstrap/dist/css/bootstrap.min.css';
import { Link, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { HomePage } from "./pages/homePage";
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import BgContext from "./components/bgContext";
import { DEFAULT_LOCALE, SUPPORTED_LOCALES, localizedPath, splitLocale } from "./seo/locales";
import { getUiStrings } from "./seo/uiStrings";
import { CATEGORY_PAGES } from "./seo/crosshairInfo";
import { initYandexMetrica, yandexMetricaHit } from "./analytics";
import { AuthProvider } from './contexts/AuthContext';
import { UserMenu } from './components/UserMenu';

const Pro = lazy(() => import("./pages/pro").then((m) => ({ default: m.Pro })));
const Fun = lazy(() => import("./pages/fun").then((m) => ({ default: m.Fun })));
const CommunityCrosshairs = lazy(() =>
  import("./pages/communityCrosshairs").then((m) => ({ default: m.CommunityCrosshairs }))
);
const CategoryCrosshairs = lazy(() =>
  import("./pages/categoryCrosshairs").then((m) => ({ default: m.CategoryCrosshairs }))
);
const CrosshairGuide = lazy(() =>
  import("./pages/crosshairGuide").then((m) => ({ default: m.CrosshairGuide }))
);
const CodeChecker = lazy(() =>
  import("./pages/codeChecker").then((m) => ({ default: m.CodeChecker }))
);
const FaqPage = lazy(() =>
  import("./pages/faqPage").then((m) => ({ default: m.FaqPage }))
);
const Editor = lazy(() =>
  import("./pages/editor").then((m) => ({ default: m.Editor }))
);
const CrosshairDetail = lazy(() =>
  import("./pages/crosshairDetail").then((m) => ({ default: m.CrosshairDetail }))
);
const SavedCrosshairs = lazy(() =>
  import("./pages/savedCrosshairs").then((m) => ({ default: m.SavedCrosshairs }))
);
const MyCrosshairSubmissions = lazy(() =>
  import("./pages/myCrosshairSubmissions").then((m) => ({ default: m.MyCrosshairSubmissions }))
);
const AdminCrosshairModeration = lazy(() =>
  import("./pages/adminCrosshairModeration").then((m) => ({ default: m.AdminCrosshairModeration }))
);
const AdminContactMessages = lazy(() =>
  import("./pages/adminContactMessages").then((m) => ({ default: m.AdminContactMessages }))
);
const AdminUsersDailyStats = lazy(() =>
  import("./pages/adminUsersDailyStats").then((m) => ({ default: m.AdminUsersDailyStats }))
);
const ContactUs = lazy(() =>
  import("./pages/contactUs").then((m) => ({ default: m.ContactUs }))
);
const Privacy = lazy(() =>
  import("./pages/privacy").then((m) => ({ default: m.Privacy }))
);
const NotFound = lazy(() =>
  import("./pages/notFound").then((m) => ({ default: m.NotFound }))
);

/** "" for the default locale (served at the root) and "/tr", "/ru" for the rest. */
const LOCALE_PREFIXES = SUPPORTED_LOCALES.map((l) => (l === DEFAULT_LOCALE ? "" : `/${l}`));

function scheduleThirdPartyWhenIdle(fn: () => void) {
  if (typeof window === "undefined") {
    return;
  }
  const w = window as Window & { requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number };
  if (w.requestIdleCallback) {
    w.requestIdleCallback(fn, { timeout: 5000 });
  } else {
    setTimeout(fn, 2_000);
  }
}

function App() {
  const { pathname, search } = useLocation();
  const { locale, basePath } = splitLocale(pathname);
  const ui = getUiStrings(locale);
  const [inputValue, setInputValue] = useState<string>("");
  const [searchValue, setSearchValue] = useState<string>("");
  const [mobileMenuVisible, setMobileMenuVisible] = useState<boolean>(false);
  const [context, setContext] = useState('default')


  // useEffect(() => {
  //   if (process.env.NODE_ENV !== "development") {
  //     return;
  //   }
  //   fetch("/api/connectivity", { cache: "no-store" })
  //     .then(async (res) => {
  //       if (!res.ok) {
  //         throw new Error(`HTTP ${res.status}`);
  //       }
  //       return res.json();
  //     })
  //     .then((payload) => console.info("[connectivity]", payload))
  //     .catch((err) =>
  //       console.warn(
  //         "[connectivity] API veya PostgreSQL erişilemedi. `backend/CrosshairValorant.AppHost` (Docker açık) ve CRA proxy portu kontrol edin.",
  //         err
  //       )
  //     );
  // }, []);

  // GA4 was initialised but never told about client-side navigations, so every route
  // change after the landing page went unrecorded. Keep the instance to send them.
  const gaRef = useRef<{ send: (fields: Record<string, unknown>) => void } | null>(null);
  const lastSentPath = useRef<string | null>(null);

  useEffect(() => {
    const initAnalyticsAndAds = () => {
      void import("react-ga4").then(({ default: ReactGA }) => {
        ReactGA.initialize("G-VYD4YM9Z3J");
        // initialize() sends a pageview for the current URL itself — record it as sent
        // so the route effect below does not double-count it.
        lastSentPath.current = window.location.pathname + window.location.search;
        gaRef.current = ReactGA;
      });
      const script = document.createElement("script");
      script.src =
        "https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=ca-pub-7472323874862675";
      script.async = true;
      script.crossOrigin = "anonymous";
      document.head.appendChild(script);
      initYandexMetrica();
    };

    const onReady = () => scheduleThirdPartyWhenIdle(initAnalyticsAndAds);

    if (document.readyState === "complete") {
      onReady();
    } else {
      window.addEventListener("load", onReady, { once: true });
    }
  }, []);

  useEffect(() => {
    const current = pathname + search;
    // Before GA loads there is nothing to send to; initialize() covers whatever route
    // is current at that moment.
    if (!gaRef.current) return;
    if (lastSentPath.current === current) return;
    const previous = lastSentPath.current;
    lastSentPath.current = current;
    gaRef.current.send({ hitType: "pageview", page: current });
    yandexMetricaHit(current, previous);
  }, [pathname, search]);

  const onInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    setInputValue(event.target.value);
  }

  const commitSearch = () => {
    setSearchValue(inputValue);
  }

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      commitSearch();
    }
  }

  const clearSearchValue = (): void => {
    setInputValue("");
    setSearchValue("");
  }

  return (
      <AuthProvider>
        <React.Fragment>
          <BgContext.Provider value={[context, setContext]}>
            <div className="App container">
              <header className="w-100 m-b-10 menu-header">
                <ul className="menu nav nav-pills">
                  <li className="nav-item menu-item">
                    <Link
                      to={localizedPath("/", locale)}
                      className={"nav-link px-2 link-light logo-placeholder"}
                      onClick={() => {
                        clearSearchValue();
                        setMobileMenuVisible(false);
                      }}
                    >
                      <img
                        src="/valorantcrosshair-header.webp"
                        width={205}
                        height={65}
                        srcSet="/valorantcrosshair-header.webp 410w, /valorantcrosshair-web.webp 1024w"
                        sizes="205px"
                        decoding="async"
                        {...({ fetchpriority: "high" } as React.ImgHTMLAttributes<HTMLImageElement>)}
                        alt="ValorantCrosshair.com — Valorant crosshair codes and generator"
                      />
                    </Link>
                    <div className='mobile-only'>
                      <UserMenu />
                    </div>
                  </li>
                  <li className="nav-item menu-item mobile-only d-flex m-b-10">
                    <div className={"icon-custom icon-menu mobile-only"} onClick={() => {
                      setMobileMenuVisible(!mobileMenuVisible);
                    }}>
                    </div>
                    {(basePath == "/" || basePath == "/valorant-pro-crosshairs" || basePath == "/valorant-fun-crosshairs" || basePath == "/valorant-community-crosshairs") &&
                      <div className="search-wrapper mobile-only">
                        <input type="text" className="form-control search-input" placeholder={ui.search} value={inputValue} onChange={onInputChange} onKeyDown={onKeyDown} />
                        <button className="search-btn" onClick={commitSearch} aria-label="Search">
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                        </button>
                      </div>
                    }
                  </li>
                  <li className={"nav-item menu-item" + (mobileMenuVisible ? " mobile-only" : " desktop-only")}>
                    <Link
                      to={localizedPath("/valorant-pro-crosshairs", locale)}
                      title="Pro crosshairs"
                      className={"nav-link px-2 link-light " + (basePath == "/valorant-pro-crosshairs" || basePath == "/pro" ? "link-selected" : "")}
                      onClick={() => {
                        clearSearchValue();
                        setMobileMenuVisible(false);
                      }}
                    >
                      {ui.nav.pro}
                    </Link>
                  </li>
                  <li className={"nav-item menu-item" + (mobileMenuVisible ? " mobile-only" : " desktop-only")}>
                    <Link
                      to={localizedPath("/valorant-fun-crosshairs", locale)}
                      title="Fun crosshairs"
                      className={"nav-link px-2 link-light " + (basePath == "/valorant-fun-crosshairs" || basePath == "/fun" ? "link-selected" : "")}
                      onClick={() => {
                        clearSearchValue();
                        setMobileMenuVisible(false);
                      }}
                    >
                      {ui.nav.fun}
                    </Link>
                  </li>
                  <li className={"nav-item menu-item" + (mobileMenuVisible ? " mobile-only" : " desktop-only")}>
                    <Link
                      to={localizedPath("/valorant-community-crosshairs", locale)}
                      title="Community crosshairs"
                      className={"nav-link px-2 link-light " + (basePath == "/valorant-community-crosshairs" ? "link-selected" : "")}
                      onClick={() => {
                        clearSearchValue();
                        setMobileMenuVisible(false);
                      }}
                    >
                      {ui.nav.community}
                    </Link>
                  </li>
                  <li className={"nav-item menu-item" + (mobileMenuVisible ? " mobile-only" : " desktop-only")}>
                    <Link
                      to={localizedPath("/valorant-crosshair-generator", locale)}
                      title="Crosshair generator"
                      className={"nav-link px-2 link-light " + (basePath == "/valorant-crosshair-generator" ? "link-selected" : "")}
                      onClick={() => {
                        setMobileMenuVisible(false);
                      }}
                    >
                      {ui.nav.generator}
                    </Link>
                  </li>
                  <li className="nav-item menu-item flex-end menu-search-item">
                    {(basePath == "/" || basePath == "/valorant-pro-crosshairs" || basePath == "/valorant-fun-crosshairs" || basePath == "/valorant-community-crosshairs") &&
                      <div className="search-wrapper desktop-only">
                        <input type="text" className="form-control search-input" placeholder={ui.search} value={inputValue} onChange={onInputChange} onKeyDown={onKeyDown} />
                        <button className="search-btn" onClick={commitSearch} aria-label="Search">
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                        </button>
                      </div>
                    }
                  </li>
                </ul>
                <div className='desktop-only'>
                  <UserMenu />
                </div>
              </header>
              <Suspense
                fallback={
                  <div className="route-suspense-fallback" role="status" aria-live="polite" aria-label="Loading" />
                }
              >
                <Routes>
                  {/* Public routes exist once per locale: "" (default, at the root) plus a
                      prefix for each translation. App/admin screens below stay unprefixed —
                      they are noindex and have no translated variant. */}
                  {LOCALE_PREFIXES.flatMap((prefix) => [
                    <Route key={`${prefix}/`} path={prefix || "/"} element={<HomePage searchValue={searchValue} clearSearchValue={clearSearchValue} />} />,
                    <Route key={`${prefix}/pro-long`} path={`${prefix}/valorant-pro-crosshairs`} element={<Pro searchValue={searchValue} clearSearchValue={clearSearchValue} />} />,
                    <Route key={`${prefix}/pro`} path={`${prefix}/pro`} element={<Pro searchValue={searchValue} clearSearchValue={clearSearchValue} />} />,
                    <Route key={`${prefix}/fun-long`} path={`${prefix}/valorant-fun-crosshairs`} element={<Fun searchValue={searchValue} clearSearchValue={clearSearchValue} />} />,
                    <Route key={`${prefix}/fun`} path={`${prefix}/fun`} element={<Fun searchValue={searchValue} clearSearchValue={clearSearchValue} />} />,
                    <Route key={`${prefix}/community`} path={`${prefix}/valorant-community-crosshairs`} element={<CommunityCrosshairs searchValue={searchValue} clearSearchValue={clearSearchValue} />} />,
                    <Route key={`${prefix}/guide`} path={`${prefix}/valorant-crosshair-guide`} element={<CrosshairGuide />} />,
                    <Route key={`${prefix}/faq`} path={`${prefix}/faq`} element={<FaqPage />} />,
                    <Route key={`${prefix}/generator`} path={`${prefix}/valorant-crosshair-generator`} element={<Editor />} />,
                    <Route key={`${prefix}/checker`} path={`${prefix}/valorant-crosshair-code-checker`} element={<CodeChecker />} />,
                    <Route key={`${prefix}/detail`} path={`${prefix}/valorant-crosshair/:slug`} element={<CrosshairDetail />} />,
                    <Route key={`${prefix}/contact`} path={`${prefix}/contact-us`} element={<ContactUs />} />,
                    <Route key={`${prefix}/privacy`} path={`${prefix}/privacy`} element={<Privacy />} />,
                    ...Object.keys(CATEGORY_PAGES).map((path) => (
                      <Route key={`${prefix}${path}`} path={`${prefix}${path}`} element={<CategoryCrosshairs path={path} />} />
                    )),
                  ])}
                  <Route path="/my-saved-crosshairs" element={<SavedCrosshairs />} />
                  <Route path="/my-crosshairs" element={<MyCrosshairSubmissions />} />
                  <Route path="/my-crosshair-submissions" element={<Navigate to="/my-crosshairs" replace />} />
                  <Route path="/admin/crosshair-moderation" element={<AdminCrosshairModeration />} />
                  <Route path="/admin/contact-messages" element={<AdminContactMessages />} />
                  <Route path="/admin/user-daily-stats" element={<AdminUsersDailyStats />} />
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </Suspense>
              <footer className="d-flex flex-wrap justify-content-between align-items-center border-top">
                <p className="col-md-4 mb-0 text-start"><strong>© 2026 ValorantCrosshair.com</strong></p>
                <ul className="nav col-md-4 justify-content-end">
                  <li className="nav-item"><Link to={localizedPath("/valorant-crosshair-code-checker", locale)} className="nav-link px-2">{ui.nav.checker}</Link></li>
                  <li className="nav-item"><Link to={localizedPath("/valorant-crosshair-guide", locale)} className="nav-link px-2">{ui.nav.guide}</Link></li>
                  <li className="nav-item"><Link to={localizedPath("/faq", locale)} className="nav-link px-2">{ui.nav.faq}</Link></li>
                  <li className="nav-item"><Link to={localizedPath("/contact-us", locale)} className="nav-link px-2">{ui.nav.contact}</Link></li>
                  <li className="nav-item"><Link to={localizedPath("/privacy", locale)} className="nav-link px-2">{ui.nav.privacy}</Link></li>
                </ul>
              </footer>
            </div>
            <ToastContainer></ToastContainer>
          </BgContext.Provider>
        </React.Fragment>
      </AuthProvider>
  );
}

export default App;
